import { getSyncStatusMessage } from "@/i18n/syncStatus";
import { sessionAtom } from "@/state/auth.state";
import { syncStateAtom } from "@/state/sync.state";
import type {
  HttpErrorBody,
  StreamCode,
  StreamEvent,
  StreamEventDone,
  StreamEventInfo,
  StreamEventSkipped,
  SyncMode,
} from "@/types/sync.types";
import {
  createMockNdjsonResponse,
  type MockSyncScenario,
} from "@/utils/mock-sync.utils";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

const mockScenario: MockSyncScenario | null = null;

type StartOptions = {
  mode?: SyncMode;
  openModal?: boolean;
};

export function useStravaSync() {
  const session = useAtomValue(sessionAtom);
  const setSync = useSetAtom(syncStateAtom);

  const queryClient = useQueryClient();

  const abortRef = useRef<AbortController | null>(null);

  const startSync = useCallback(
    async (opts?: StartOptions) => {
      if (!session) return;

      const mode: SyncMode = opts?.mode ?? "normal";
      const openModal = opts?.openModal ?? true;

      // Prevent double runs
      let shouldStart = true;
      setSync((s) => {
        if (s.isRunning) {
          shouldStart = false;
          return s;
        }
        return {
          ...s,
          isRunning: true,
          syncAttemptCompleted: false,
          modalOpen: openModal ? true : s.modalOpen,
          status: "Starting sync…",
          error: null,
          progress: { page: 0, activities: 0 },
          events: [],
          lastResult: null,
        };
      });
      if (!shouldStart) return;

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      void queryClient.removeQueries({
        predicate: (q) => {
          const key = q.queryKey;
          const root = Array.isArray(key) ? key[0] : key;
          return (
            root === "calendar-activities" ||
            root === "chart-data" ||
            root === "map-activities" ||
            root === "table-activities"
          );
        },
      });

      const pushEvent = (evt: StreamEvent) => {
        setSync((s) => ({ ...s, events: [...s.events, evt] }));
      };

      const warningCodes: StreamCode[] = [];

      try {
        const body =
          mode === "full_refresh"
            ? { force_refresh: true }
            : mode === "force_fetch"
              ? { force_fetch: true }
              : { force_fetch: false };

        const response = mockScenario
          ? createMockNdjsonResponse(
              {
                scenario: mockScenario,
              },
              ac.signal,
            )
          : await fetch(`${supabaseUrl}/functions/v1/strava-sync`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(body),
              signal: ac.signal,
            });

        if (!response.ok) {
          // HTTP-level error: expect { code, status, ... } now
          let code = `http.${response.status}`;
          try {
            const data = (await response.json()) as HttpErrorBody;
            if (data?.code) code = data.code;
          } catch {
            // ignore
          }
          throw new Error(code);
        }

        const contentType = response.headers.get("content-type") ?? "";
        if (!contentType.includes("application/x-ndjson")) {
          // Not an NDJSON stream; try to parse and surface a code
          try {
            const data = (await response.json()) as HttpErrorBody;
            throw new Error(data?.code ?? "sync.error.expected_ndjson");
          } catch {
            throw new Error("sync.error.expected_ndjson");
          }
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("sync.error.no_stream_body");

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let newlineIdx: number;
          while ((newlineIdx = buffer.indexOf("\n")) >= 0) {
            const line = buffer.slice(0, newlineIdx).trim();
            buffer = buffer.slice(newlineIdx + 1);
            if (!line) continue;

            let evt: StreamEvent;
            try {
              evt = JSON.parse(line) as StreamEvent;
            } catch {
              continue;
            }

            pushEvent(evt);

            // Fatal stream error event
            if (evt.type === "error") {
              throw new Error(evt.code);
            }

            // Track warnings
            if (evt.type === "warning") {
              warningCodes.push(evt.code);
            }

            setSync((s) => {
              const nextStatus = getSyncStatusMessage(evt, mode);

              switch (evt.type) {
                case "start":
                  return {
                    ...s,
                    status: nextStatus,
                  };

                case "skipped":
                  const skippedEvent = evt as StreamEventSkipped;
                  return {
                    ...s,
                    status: nextStatus,
                    lastSyncedAt: skippedEvent.details?.last_synced_at ?? null,
                  };

                case "info":
                  if (evt.code === "sync.page.fetched") {
                    const pageFetchedEvent =
                      evt as StreamEventInfo<"sync.page.fetched">;
                    return {
                      ...s,
                      status: nextStatus,
                      progress: {
                        page: pageFetchedEvent.details?.pages_fetched ?? 0,
                        activities:
                          pageFetchedEvent.details?.activities_fetched ?? 0,
                      },
                    };
                  }

                  return {
                    ...s,
                    status: nextStatus,
                  };

                case "warning":
                  return {
                    ...s,
                    status: nextStatus,
                  };

                case "done": {
                  const doneEvent = evt as StreamEventDone;

                  // Merge warning codes from mid-stream and from done payload
                  const mergedWarnings = Array.from(
                    new Set([
                      ...(warningCodes ?? []),
                      ...(doneEvent.warning_codes ?? []),
                    ]),
                  );

                  // Invalidate app data after a successful sync
                  if (doneEvent.ok) {
                    void queryClient.invalidateQueries({
                      predicate: (q) => {
                        const key = q.queryKey;
                        const root = Array.isArray(key) ? key[0] : key;
                        return (
                          root === "calendar-activities" ||
                          root === "chart-data" ||
                          root === "map-activities" ||
                          root === "table-activities"
                        );
                      },
                    });
                  }

                  return {
                    ...s,
                    isRunning: false,
                    syncAttemptCompleted: true,
                    status: nextStatus,
                    lastSyncedAt:
                      (doneEvent.details?.last_synced_at || s.lastSyncedAt) ??
                      null,
                    lastResult: { ok: doneEvent.ok },
                    warnings: mergedWarnings,
                  };
                }

                default:
                  return s;
              }
            });
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);

        setSync((s) => ({
          ...s,
          isRunning: false,
          syncAttemptCompleted: true,
          error: message, // this is a CODE now (e.g. sync.error.upsert_failed)
          status: "Sync failed.",
          lastResult: { ok: false, mode },
        }));
      } finally {
        abortRef.current = null;
      }
    },
    [session, setSync, queryClient],
  );

  const closeModal = useCallback(() => {
    setSync((s) => ({ ...s, modalOpen: false }));
  }, [setSync]);

  const openModal = useCallback(() => {
    setSync((s) => ({ ...s, modalOpen: true }));
  }, [setSync]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Auto-run once per app session when session becomes available
  useEffect(() => {
    if (!session) return;

    let shouldKickoff = false;
    setSync((s) => {
      if (s.hasAutoRunOnce) return s;
      shouldKickoff = true;
      return { ...s, hasAutoRunOnce: true };
    });

    if (shouldKickoff) {
      void startSync({ mode: "normal", openModal: true });
    }
  }, [session, setSync, startSync]);

  return { startSync, closeModal, openModal, abort };
}
